"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { X, Mail, CheckCircle } from "lucide-react";
import { RemoveScroll } from "react-remove-scroll";
import { AnimatePresence, motion } from "framer-motion";
import { newsletterSubscribeSchema } from "@/lib/validation/newsletter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { VerifyEmailStep } from "./VerifyEmailStep";
import { InterestsStep } from "./InterestsStep";
export type ThemeClasses = {
  bg: string;
  text: string;
  border: string;
  borderFocus: string;
  hoverBg: string;
  hoverText: string;
  bgLight: string;
  bgLight10: string;
  font: string;
  gradient: string;
};
const getDomainTheme = (domain: string): ThemeClasses => {
  if (domain.includes("jejutime")) {
    return {
      bg: "bg-blue-600",
      text: "text-blue-600",
      border: "border-blue-600",
      borderFocus: "focus:border-blue-600",
      hoverBg: "hover:bg-blue-700",
      hoverText: "hover:text-blue-600",
      bgLight: "bg-blue-50",
      bgLight10: "bg-blue-600/10",
      font: "font-serif",
      gradient: "from-blue-600/5"
    };
  }
  if (domain.includes("jejujapan")) {
    return {
      bg: "bg-[#bc002d]",
      text: "text-[#bc002d]",
      border: "border-[#bc002d]",
      borderFocus: "focus:border-[#bc002d]",
      hoverBg: "hover:bg-[#9a0025]",
      hoverText: "hover:text-[#bc002d]",
      bgLight: "bg-[#bc002d]/5",
      bgLight10: "bg-[#bc002d]/10",
      font: "font-serif",
      gradient: "from-[#bc002d]/5"
    };
  }
  if (domain.includes("jejuqq")) {
    return {
      bg: "bg-[#e60012]",
      // Pure red for JejuQQ
      text: "text-[#e60012]",
      border: "border-[#e60012]",
      borderFocus: "focus:border-[#e60012]",
      hoverBg: "hover:bg-[#cc0010]",
      hoverText: "hover:text-[#e60012]",
      bgLight: "bg-red-50",
      bgLight10: "bg-[#e60012]/10",
      font: "font-serif",
      gradient: "from-[#e60012]/5"
    };
  }
  if (domain.includes("voicejeju")) {
    return {
      bg: "bg-black",
      text: "text-black",
      border: "border-black",
      borderFocus: "focus:border-black",
      hoverBg: "hover:bg-gray-900",
      hoverText: "hover:text-black",
      bgLight: "bg-gray-50",
      bgLight10: "bg-black/10",
      font: "font-inter",
      gradient: "from-black/5"
    };
  }
  if (domain.includes("skyblueprime")) {
    return {
      bg: "bg-sky-950",
      text: "text-sky-950",
      border: "border-sky-950",
      borderFocus: "focus:border-sky-950",
      hoverBg: "hover:bg-sky-900",
      hoverText: "hover:text-sky-950",
      bgLight: "bg-sky-50",
      bgLight10: "bg-sky-950/10",
      font: "font-sans",
      gradient: "from-sky-950/5"
    };
  }
  if (domain.includes("lavaguetech")) {
    return {
      bg: "bg-red-600",
      text: "text-red-600",
      border: "border-red-600",
      borderFocus: "focus:border-red-600",
      hoverBg: "hover:bg-red-500",
      hoverText: "hover:text-red-600",
      bgLight: "bg-red-50",
      bgLight10: "bg-red-600/10",
      font: "font-sans",
      gradient: "from-red-600/5"
    };
  }
  // Default / NewsIcons
  return {
    bg: "bg-[#ff4500]",
    text: "text-[#ff4500]",
    border: "border-[#ff4500]",
    borderFocus: "focus:border-[#ff4500]",
    hoverBg: "hover:bg-[#e63e00]",
    hoverText: "hover:text-[#ff4500]",
    bgLight: "bg-orange-50",
    bgLight10: "bg-[#ff4500]/10",
    font: "font-sans",
    gradient: "from-[#ff4500]/5"
  };
};
interface NewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
  domain?: string;
}
type CategoryOption = {
  id: string;
  name: string;
};
export function NewsletterModal(t0) {
  const $ = _c(109);
  const {
    isOpen,
    onClose,
    domain: t1
  } = t0;
  const domain = t1 === undefined ? "" : t1;
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [selectedInterests, setSelectedInterests] = useState(t2);
  const [otpSendError, setOtpSendError] = useState("");
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["", "", "", "", "", ""];
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [otp, setOtp] = useState(t3);
  const [otpError, setOtpError] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const otpInputs = useRef(t4);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [localDomain, setLocalDomain] = useState(domain);
  let t5;
  let t6;
  if ($[3] !== domain) {
    t5 = () => {
      if (domain) {
        setLocalDomain(domain);
      } else {
        setLocalDomain(window.location.hostname);
      }
    };
    t6 = [domain];
    $[3] = domain;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  if ($[6] !== localDomain) {
    t7 = getDomainTheme(localDomain.toLowerCase());
    $[6] = localDomain;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const theme = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["newsletterCategories"];
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== isOpen) {
    t9 = {
      queryKey: t8,
      queryFn: _temp,
      enabled: isOpen
    };
    $[9] = isOpen;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const {
    data: t10,
    isLoading: categoriesLoading,
    isError: categoriesIsError,
    error: categoriesError
  } = useQuery(t9);
  let t11;
  if ($[11] !== t10) {
    t11 = t10 === undefined ? [] : t10;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const categories = t11;
  let t12;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      mutationFn: _temp2
    };
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const checkEmailMutation = useMutation(t12);
  let t13;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      mutationFn: _temp4,
      onSuccess: () => {
        setOtpSendError("");
        setStep("verification");
        setTimeout(() => otpInputs.current[0]?.focus(), 100);
      },
      onError: err => {
        setOtpSendError(err.message || "Failed to send verification code.");
      }
    };
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const sendOtpMutation = useMutation(t13);
  let t14;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      mutationFn: _temp6,
      onSuccess: () => {
        setOtpError(false);
        setOtpErrorMessage("");
        setStep("success");
      },
      onError: err_0 => {
        setOtpError(true);
        setOtpErrorMessage(err_0.message || "Invalid verification code. Please try again.");
      }
    };
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  const verifyOtpMutation = useMutation(t14);
  const categoriesErrorMessage = categoriesIsError ? categoriesError?.message ?? "Failed to load categories" : "";
  let t15;
  let t16;
  if ($[16] !== resendCooldown) {
    t15 = () => {
      if (resendCooldown <= 0) {
        return;
      }
      const timer = setInterval(() => {
        setResendCooldown(_temp7);
      }, 1000);
      return () => clearInterval(timer);
    };
    t16 = [resendCooldown];
    $[16] = resendCooldown;
    $[17] = t15;
    $[18] = t16;
  } else {
    t15 = $[17];
    t16 = $[18];
  }
  useEffect(t15, t16);
  let t17;
  if ($[19] !== checkEmailMutation || $[20] !== email) {
    t17 = async e => {
      e.preventDefault();
      const parsed = newsletterSubscribeSchema.safeParse({
        email
      });
      if (!parsed.success) {
        const fieldErrors = parsed.error.flatten().fieldErrors.email;
        setError(fieldErrors?.[0] ?? "Invalid email");
        return;
      }
      ;
      try {
        setError("");
        const data_1 = await checkEmailMutation.mutateAsync(parsed.data.email);
        if (data_1.subscribed) {
          setError("Email is already subscribed");
          return;
        }
        setStep("interests");
      } catch (t18) {
        const err_1 = t18;
        console.error("Check email error:", err_1);
        setError("Something went wrong. Please try again.");
      }
    };
    $[19] = checkEmailMutation;
    $[20] = email;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const handleEmailSubmit = t17;
  let t18;
  if ($[22] !== email || $[23] !== sendOtpMutation) {
    t18 = async () => {
      if (!email) {
        return;
      }
      ;
      try {
        setOtpSendError("");
        await sendOtpMutation.mutateAsync(email);
      } catch (t19) {
        const err_2 = t19;
        console.error("Failed to send OTP:", err_2);
      }
    };
    $[22] = email;
    $[23] = sendOtpMutation;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  const handleInterestsSubmit = t18;
  let t19;
  if ($[25] !== email || $[26] !== otp || $[27] !== selectedInterests || $[28] !== verifyOtpMutation) {
    t19 = async e_0 => {
      e_0?.preventDefault();
      const code_0 = otp.join("");
      if (!email || code_0.length !== 6) {
        setOtpError(true);
        return;
      }
      ;
      try {
        setOtpError(false);
        await verifyOtpMutation.mutateAsync({
          email,
          code: code_0,
          categories: selectedInterests
        });
      } catch (t20) {
        const err_3 = t20;
        console.error("Verify OTP error:", err_3);
      }
    };
    $[25] = email;
    $[26] = otp;
    $[27] = selectedInterests;
    $[28] = verifyOtpMutation;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  const handleVerificationSubmit = t19;
  let t20;
  if ($[30] !== onClose) {
    t20 = () => {
      onClose();
      setTimeout(() => {
        setStep("email");
        setEmail("");
        setSelectedInterests([]);
        setError("");
        setOtp(["", "", "", "", "", ""]);
        setOtpError(false);
        setOtpErrorMessage("");
        setOtpSendError("");
        setResendCooldown(0);
      }, 300);
    };
    $[30] = onClose;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  const handleFinalClose = t20;
  let t21;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = interestId => {
      setSelectedInterests(prev_0 => prev_0.includes(interestId) ? prev_0.filter(i => i !== interestId) : [...prev_0, interestId]);
    };
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  const toggleInterest = t21;
  let t22;
  if ($[33] !== otp || $[34] !== otpError || $[35] !== otpErrorMessage) {
    t22 = (index, value) => {
      if (otpError) {
        setOtpError(false);
      }
      if (otpErrorMessage) {
        setOtpErrorMessage("");
      }
      const digit = value.replace(/\D/g, "").slice(-1);
      if (!digit && value !== "") {
        return;
      }
      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);
      if (digit && index < 5) {
        otpInputs.current[index + 1]?.focus();
      }
    };
    $[33] = otp;
    $[34] = otpError;
    $[35] = otpErrorMessage;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const handleOtpChange = t22;
  let t23;
  if ($[37] !== otp) {
    t23 = (index_0, e_1) => {
      if (e_1.key === "Backspace" && !otp[index_0] && index_0 > 0) {
        otpInputs.current[index_0 - 1]?.focus();
      }
    };
    $[37] = otp;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const handleKeyDown = t23;
  if (!isOpen) {
    return null;
  }
  const t24 = `flex items-center justify-between p-4 sm:p-5 ${theme.bg} rounded-t-2xl shrink-0`;
  const t25 = `text-xl sm:text-2xl font-bold text-white ${theme.font}`;
  const t26 = step === "email" && "Subscribe to Newsletter";
  const t27 = step === "verification" && "Verify Your Email";
  const t28 = step === "interests" && "Choose Your Interests";
  const t29 = step === "success" && "You're All Set!";
  let t30;
  if ($[39] !== t25 || $[40] !== t26 || $[41] !== t27 || $[42] !== t28 || $[43] !== t29) {
    t30 = <h2 className={t25}>{t26}{t27}{t28}{t29}</h2>;
    $[39] = t25;
    $[40] = t26;
    $[41] = t27;
    $[42] = t28;
    $[43] = t29;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  let t31;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />;
    $[45] = t31;
  } else {
    t31 = $[45];
  }
  let t32;
  if ($[46] !== onClose) {
    t32 = <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors inline-flex items-center justify-center">{t31}</button>;
    $[46] = onClose;
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  let t33;
  if ($[48] !== t24 || $[49] !== t30 || $[50] !== t32) {
    t33 = <div className={t24}>{t30}{t32}</div>;
    $[48] = t24;
    $[49] = t30;
    $[50] = t32;
    $[51] = t33;
  } else {
    t33 = $[51];
  }
  let t34;
  if ($[52] !== email || $[53] !== error || $[54] !== handleEmailSubmit || $[55] !== onClose || $[56] !== step || $[57] !== theme.bg || $[58] !== theme.font || $[59] !== theme.hoverBg || $[60] !== theme.text) {
    t34 = step === "email" && <motion.div key="email" initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 20
    }} transition={{
      duration: 0.2
    }}><form noValidate={true} onSubmit={handleEmailSubmit}><div className="relative mb-3 sm:mb-4"><Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 ${theme.text}`} /><input type="email" value={email} onChange={e_3 => {
            setEmail(e_3.target.value);
            if (error) {
              setError("");
            }
          }} placeholder="Enter your email address" autoFocus={true} className={`w-full thick-border pl-10 sm:pl-12 pr-4 py-3 sm:py-3.5 text-sm sm:text-base bg-gray-100 border-2 rounded-lg focus:bg-white focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all placeholder:text-gray-500 ${theme.font} ${error ? "border-red-500 bg-red-50" : "border-transparent"}`} /></div>{error && <p className={`text-xs sm:text-sm text-red-500 mb-3 sm:mb-4 ${theme.font}`}>{error}</p>}<div className="flex gap-2 sm:gap-3 mb-4 sm:mb-6"><button type="submit" className={`flex-1 ${theme.bg} text-white px-4 sm:px-6 py-3 sm:py-3.5 rounded-lg ${theme.hoverBg} transition-colors font-medium text-sm sm:text-base ${theme.font}`}>Next</button><button type="button" onClick={onClose} className={`px-6 sm:px-8 py-3 sm:py-3.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 font-medium text-sm sm:text-base ${theme.font}`}>Cancel</button></div></form><div className="pt-4 sm:pt-6 border-t border-gray-200"><h3 className={`text-xs sm:text-sm font-semibold text-gray-900 mb-3 sm:mb-4 ${theme.font}`}>What you'll get</h3><ul className="space-y-2 sm:space-y-3">{["Daily curated news and trending products", "AI-powered insights and analysis", "Exclusive content across 8+ categories"].map(benefit => <li key={benefit} className="flex items-start gap-2 sm:gap-3"><span className={`${theme.text} mt-0.5 flex-shrink-0 text-xs sm:text-sm`}>✓</span><span className={`text-xs sm:text-sm text-gray-700 ${theme.font}`}>{benefit}</span></li>)}</ul></div></motion.div>;
    $[52] = email;
    $[53] = error;
    $[54] = handleEmailSubmit;
    $[55] = onClose;
    $[56] = step;
    $[57] = theme.bg;
    $[58] = theme.font;
    $[59] = theme.hoverBg;
    $[60] = theme.text;
    $[61] = t34;
  } else {
    t34 = $[61];
  }
  let t35;
  if ($[62] !== email || $[63] !== handleKeyDown || $[64] !== handleOtpChange || $[65] !== handleVerificationSubmit || $[66] !== otp || $[67] !== otpError || $[68] !== otpErrorMessage || $[69] !== otpSendError || $[70] !== resendCooldown || $[71] !== sendOtpMutation || $[72] !== step || $[73] !== theme || $[74] !== verifyOtpMutation) {
    t35 = step === "verification" && <VerifyEmailStep email={email} otp={otp} otpError={otpError} otpErrorMessage={otpErrorMessage} resendCooldown={resendCooldown} otpSendError={otpSendError} isVerifying={verifyOtpMutation.isPending} otpInputsRef={otpInputs} onBack={() => setStep("interests")} onSubmit={handleVerificationSubmit} onOtpChange={handleOtpChange} onKeyDown={handleKeyDown} onResend={async () => {
      setOtp(["", "", "", "", "", ""]);
      setOtpError(false);
      otpInputs.current[0]?.focus();
      ;
      try {
        await sendOtpMutation.mutateAsync(email);
        setTimeout(() => otpInputs.current[0]?.focus(), 100);
        setResendCooldown(120);
      } catch (t36) {
        const err_4 = t36;
        console.error("Failed to resend OTP", err_4);
      }
    }} theme={theme} />;
    $[62] = email;
    $[63] = handleKeyDown;
    $[64] = handleOtpChange;
    $[65] = handleVerificationSubmit;
    $[66] = otp;
    $[67] = otpError;
    $[68] = otpErrorMessage;
    $[69] = otpSendError;
    $[70] = resendCooldown;
    $[71] = sendOtpMutation;
    $[72] = step;
    $[73] = theme;
    $[74] = verifyOtpMutation;
    $[75] = t35;
  } else {
    t35 = $[75];
  }
  let t36;
  if ($[76] !== categories || $[77] !== categoriesErrorMessage || $[78] !== categoriesLoading || $[79] !== handleInterestsSubmit || $[80] !== onClose || $[81] !== otpSendError || $[82] !== selectedInterests || $[83] !== sendOtpMutation || $[84] !== step || $[85] !== theme) {
    t36 = step === "interests" && <InterestsStep categories={categories} categoriesLoading={categoriesLoading} categoriesErrorMessage={categoriesErrorMessage} selectedInterests={selectedInterests} otpSendError={otpSendError} isSendingOtp={sendOtpMutation.isPending} onBack={() => setStep("email")} onToggleInterest={toggleInterest} onSubmit={handleInterestsSubmit} onCancel={onClose} theme={theme} />;
    $[76] = categories;
    $[77] = categoriesErrorMessage;
    $[78] = categoriesLoading;
    $[79] = handleInterestsSubmit;
    $[80] = onClose;
    $[81] = otpSendError;
    $[82] = selectedInterests;
    $[83] = sendOtpMutation;
    $[84] = step;
    $[85] = theme;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== handleFinalClose || $[88] !== step || $[89] !== theme.bg || $[90] !== theme.bgLight10 || $[91] !== theme.font || $[92] !== theme.hoverBg || $[93] !== theme.text) {
    t37 = step === "success" && <motion.div key="success" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      type: "spring",
      damping: 20,
      stiffness: 300
    }} className="text-center py-4"><div className="flex justify-center mb-4 sm:mb-6"><div className={`w-12 h-12 sm:w-16 sm:h-16 ${theme.bgLight10} rounded-2xl flex items-center justify-center`}><CheckCircle className={`w-6 h-6 sm:w-8 sm:h-8 ${theme.text}`} /></div></div><h3 className={`text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3 ${theme.font}`}>Subscription Successful</h3><p className={`text-xs sm:text-base text-gray-600 mb-6 sm:mb-8 ${theme.font}`}>You're now subscribed to our newsletter</p><button type="button" onClick={handleFinalClose} className={`w-full px-4 sm:px-6 py-3 sm:py-3.5 rounded-lg font-bold text-base sm:text-lg ${theme.bg} text-white ${theme.hoverBg} transition-all active:scale-[0.98] ${theme.font}`}>Close</button></motion.div>;
    $[87] = handleFinalClose;
    $[88] = step;
    $[89] = theme.bg;
    $[90] = theme.bgLight10;
    $[91] = theme.font;
    $[92] = theme.hoverBg;
    $[93] = theme.text;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== t34 || $[96] !== t35 || $[97] !== t36 || $[98] !== t37) {
    t38 = <div className="p-5 sm:p-8 overflow-y-auto min-h-0"><AnimatePresence mode="wait">{t34}{t35}{t36}{t37}</AnimatePresence></div>;
    $[95] = t34;
    $[96] = t35;
    $[97] = t36;
    $[98] = t37;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  let t39;
  if ($[100] !== t33 || $[101] !== t38) {
    t39 = <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl relative overflow-hidden flex flex-col max-h-[90dvh]" onClick={_temp8}>{t33}{t38}</div>;
    $[100] = t33;
    $[101] = t38;
    $[102] = t39;
  } else {
    t39 = $[102];
  }
  let t40;
  if ($[103] !== onClose || $[104] !== t39) {
    t40 = <div className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm transition-opacity flex items-center justify-center p-4" onClick={onClose}>{t39}</div>;
    $[103] = onClose;
    $[104] = t39;
    $[105] = t40;
  } else {
    t40 = $[105];
  }
  let t41;
  if ($[106] !== isOpen || $[107] !== t40) {
    t41 = <><RemoveScroll enabled={isOpen}>{t40}</RemoveScroll></>;
    $[106] = isOpen;
    $[107] = t40;
    $[108] = t41;
  } else {
    t41 = $[108];
  }
  return t41;
}
function _temp8(e_2) {
  return e_2.stopPropagation();
}
function _temp7(prev) {
  return prev > 0 ? prev - 1 : 0;
}
async function _temp6(t0) {
  const {
    email: email_0,
    code,
    categories: categories_0
  } = t0;
  const res_2 = await fetch("/api/newsletter/verify-otp", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      email: email_0,
      code,
      categories: categories_0
    })
  });
  if (!res_2.ok) {
    const data_0 = await res_2.json().catch(_temp5);
    const message_0 = typeof (data_0 as {
      error?: unknown;
    }).error === "string" ? (data_0 as {
      error: string;
    }).error : "Invalid verification code. Please try again.";
    throw new Error(message_0);
  }
}
function _temp5() {
  return {};
}
async function _temp4(emailToSend) {
  const res_1 = await fetch("/api/newsletter/send-otp", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      email: emailToSend
    })
  });
  if (!res_1.ok) {
    const data = await res_1.json().catch(_temp3);
    const message = typeof (data as {
      error?: unknown;
    }).error === "string" ? (data as {
      error: string;
    }).error : "Failed to send verification code.";
    throw new Error(message);
  }
}
function _temp3() {
  return {};
}
async function _temp2(emailToCheck) {
  const res_0 = await fetch("/api/newsletter/check-email", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      email: emailToCheck
    })
  });
  if (!res_0.ok) {
    throw new Error("Something went wrong. Please try again.");
  }
  return (await res_0.json()) as {
    subscribed?: boolean;
  };
}
async function _temp() {
  const res = await fetch("/api/categories");
  if (!res.ok) {
    throw new Error("Failed to load categories");
  }
  return (await res.json()) as CategoryOption[];
}